<template>
 <!-- eslint-disable vue/no-mutating-props -->
 <a-form-item
  v-if="visible"
  :label="item.displayName"
  :name="fieldName"
  :rules="rules"
 >
  <DatePickerWrapper v-if="item.inputType === 'STARTDATE'">
   <a-date-picker
    v-model:value="item.value"
    :defaultPickerValue="getDefaultPicker()"
    :disabled="disabled"
    :disabled-date="disabledStartDate"
    :format="dateFormat"
    :locale="locale"
    class="w-100"
   />
  </DatePickerWrapper>

  <DatePickerWrapper v-else-if="item.inputType === 'DATE'">
   <a-date-picker
    v-model:value="item.value"
    :defaultPickerValue="moment()"
    :disabled="disabled"
    :format="dateFormat"
    :locale="locale"
    class="w-100"
   />
  </DatePickerWrapper>

  <a-input
   v-else-if="item.inputType === 'TEXT'"
   v-model:value="item.value"
   :disabled="disabled"
   placeholder=""
  />

  <a-input
   v-else-if="item.inputType === 'INTEGER' && item.type !== 'PANELCOUNT'"
   v-model:value="item.value"
   :disabled="disabled"
   placeholder=""
   step="1"
   type="text"
   onkeydown="if(event.key==='.'||event.key===','){event.preventDefault();}"
  />

  <a-input
   v-else-if="item.inputType === 'INTEGER' && item.type === 'PANELCOUNT'"
   v-model:value="item.value"
   :disabled="true"
   placeholder=""
   step="1"
   type="text"
   onkeydown="if(event.key==='.'||event.key===','){event.preventDefault();}"
  />

  <a-input
   v-else-if="item.inputType === 'AMOUNT'"
   v-model:value="item.value"
   :disabled="disabled"
   placeholder=""
   type="text"
  />

  <a-select
   v-else-if="item.inputType === 'SELECT'"
   v-model:value="item.value"
   :allowClear="true"
   :disabled="disabled"
   class="sDash_fullwidth-select"
   size="large"
  >
   <a-select-option
    v-for="(option, optionIndex) in selectOptions"
    :key="optionIndex"
    :value="option.value"
   >
    {{ option.label }}
   </a-select-option>
  </a-select>

  <a-select
   v-else-if="item.inputType === 'MULTISELECT'"
   v-model:value="item.value"
   :allowClear="true"
   :disabled="disabled"
   class="sDash_fullwidth-select"
   mode="multiple"
   size="large"
  >
   <a-select-option
    v-for="(option, optionIndex) in selectOptions"
    :key="optionIndex"
    :value="option.value"
   >
    {{ option.label }}
   </a-select-option>
  </a-select>
 </a-form-item>
</template>

<script>
import {computed, onMounted, watch} from 'vue';
import moment from 'moment';
import {DatePickerWrapper} from '@/view/styled';
import {amountValidator, integerValidator} from '@/utility/validators';
import {
 flowQuestionRequiredRules,
 normalizeFlowQuestionSelectOptions,
} from '@/utility/flowQuestion';

export default {
 name: 'FlowQuestionFormItem',
 components: {
  DatePickerWrapper,
 },
 props: {
  item: {
   type: Object,
   required: true,
  },
  index: {
   type: Number,
   required: true,
  },
  namePrefix: {
   type: String,
   default: 'flowQuestions',
  },
  disabled: {
   type: Boolean,
   default: false,
  },
  locale: {
   type: Object,
   default: null,
  },
 },
 setup(props) {
  const dateFormat = 'DD-MM-YYYY';

  const supportedInputTypes = [
   'STARTDATE',
   'DATE',
   'TEXT',
   'INTEGER',
   'SELECT',
   'MULTISELECT',
   'AMOUNT',
  ];

  const visible = computed(() => supportedInputTypes.includes(props.item?.inputType));

  const fieldName = computed(() => [props.namePrefix, props.index, 'value']);

  const selectOptions = computed(() => normalizeFlowQuestionSelectOptions(props.item?.inputValues));

  const rules = computed(() => {
   const requiredRules = flowQuestionRequiredRules(props.item);

   if (props.item?.inputType === 'INTEGER' && props.item?.type !== 'PANELCOUNT') {
    return [...requiredRules, {validator: integerValidator}];
   }

   if (props.item?.inputType === 'AMOUNT') {
    return [...requiredRules, {validator: amountValidator}];
   }

   return requiredRules;
  });

  const ensureMultiSelectValue = () => {
   if (props.item?.inputType !== 'MULTISELECT') {
    return;
   }

   if (!Array.isArray(props.item.value)) {
    // eslint-disable-next-line vue/no-mutating-props
    props.item.value = [];
   }
  };

  onMounted(ensureMultiSelectValue);
  watch(() => props.item?.inputType, ensureMultiSelectValue);

  const getDefaultPicker = () => moment().add(1, 'day');

  const disabledStartDate = (current) => current && current < moment().endOf('day');

  return {
   visible,
   fieldName,
   selectOptions,
   rules,
   dateFormat,
   getDefaultPicker,
   disabledStartDate,
   moment,
  };
 },
};
</script>
